import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import { ChevronRight } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useImage from 'services/Hooks/useImage';
import { getNavLinkBehavior } from 'services/Utilities/navLinkUtils';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
  },
  navItem: {
    height: ({ rowHeight }) => rowHeight,
    padding: '0 16px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.05rem',
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    color: ({ color }) => color,
    transition: 'background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  navItemText: {
    color: 'inherit',
    transition: 'color 0.18s ease',
  },
  navItemActive: {
    backgroundColor: '#f2f2f2',
    color: theme.palette.secondary.main,
    boxShadow: `0 10px 16px ${alpha(theme.palette.common.black, 0.2)}`,
    position: 'relative',
    zIndex: 2,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
    '& $navItemText': {
      color: `${theme.palette.secondary.main} !important`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: -1,
      height: 2,
      backgroundColor: '#f2f2f2',
    },
  },
  popoverPaper: {
    backgroundColor: '#ebebeb',
    color: '#1f1f1f',
    boxShadow: theme.shadows[8],
    borderRadius: 0,
    width: '100vw',
    maxWidth: '100vw',
    overflow: 'hidden',
  },
  megaContent: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 320px) minmax(0, 320px) minmax(0, 1fr)',
    minHeight: 360,
    maxHeight: 'min(68vh, 640px)',
    width: 'min(1480px, 100%)',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  column: {
    padding: '18px 20px',
    borderRight: 'none',
    overflowY: 'auto',
    boxSizing: 'border-box',
    minWidth: 0,
  },
  secondColumn: {
    backgroundColor: 'transparent',
    borderLeft: `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
  },
  thirdColumn: {
    borderRight: 'none',
    padding: 0,
    backgroundColor: 'transparent',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    minWidth: 0,
  },
  columnTitle: {
    textTransform: 'uppercase',
    letterSpacing: '0.06rem',
    fontWeight: 700,
    color: theme.palette.secondary.main,
    marginBottom: 10,
  },
  menuLink: {
    display: 'block',
    color: 'inherit',
    textDecoration: 'none',
  },
  menuButton: {
    width: '100%',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.03rem',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.16),
    },
  },
  menuButtonActive: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.black,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transition: 'transform 0.22s ease',
  },
  imagePreviewCard: {
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
    boxShadow: 'none',
  },
  imagePreviewMedia: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: 336,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    padding: 0,
    transition: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:hover img': {
      transform: 'scale(1.01)',
    },
  },
  imagePreviewWrap: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
  },
  previewOverlay: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: 'fit-content',
    maxWidth: '100%',
    backgroundColor: alpha(theme.palette.secondary.main, 0.8),
    color: theme.palette.common.black,
    borderRadius: 0,
    padding: 0,
    backdropFilter: 'blur(1px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  previewCaption: {
    margin: 0,
    padding: '6px 12px 8px',
    textAlign: 'left',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04rem',
    color: 'inherit',
    lineHeight: 1.2,
  },

  imageFallback: {
    width: '100%',
    height: '100%',
    minHeight: 336,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    color: alpha(theme.palette.common.black, 0.52),
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    textTransform: 'none',
    letterSpacing: '0.01rem',
    fontWeight: 500,
    padding: 8,
    textAlign: 'center',
  },
  noThirdLevel: {
    color: alpha(theme.palette.common.black, 0.6),
    textTransform: 'uppercase',
    letterSpacing: '0.05rem',
    padding: '8px 10px',
    fontWeight: 500,
  },
}));

const OPEN_DELAY = 50;
const CLOSE_DELAY = 250;
const TOUCH_INPUT_QUERY = '(hover: none), (pointer: coarse)';

const MegaSiteNav = ({ items, align, background, color, contentBackground, contentColor, rowHeight, breakpoint, style }) => {
  const { t } = useTranslation();
  const matches = useMediaQuery(`(min-width:${breakpoint}px)`);
  const isTouchInput = useMediaQuery(TOUCH_INPUT_QUERY);
  const classes = useStyles({ color, contentBackground, contentColor, rowHeight });
  const { getDealerPortalMenuImage } = useImage();
  const rootRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTop, setActiveTop] = useState(null);
  const [activeSecond, setActiveSecond] = useState(null);
  const [activeThird, setActiveThird] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);

  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const sortedItems = useMemo(() => (items || []).slice().sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)), [
    items,
  ]);

  const getSortedChildren = menuNode =>
    (Array.isArray(menuNode?.children) ? menuNode.children : [])
      .slice()
      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
  const isTouchInteractionMode = matches && isTouchInput;

  const getPreviewFileId = menuNode => menuNode?.menuAttachedFiles?.[0]?.id || null;

  const getNodePreview = menuNode => {
    const previewFileId = getPreviewFileId(menuNode);
    if (!menuNode?.id || !previewFileId) {
      return null;
    }

    return getDealerPortalMenuImage(menuNode.id, previewFileId);
  };

  const clearAllTimers = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }

    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const closeMenu = () => {
    clearAllTimers();
    setActiveTop(null);
    setActiveSecond(null);
    setActiveThird(null);
    setPreviewSrc(null);
    setAnchorEl(null);
  };

  const scheduleClose = () => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }

    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
    }

    closeTimer.current = setTimeout(() => {
      closeMenu();
    }, CLOSE_DELAY);
  };

  const handlePopoverEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleClickAway = event => {
    if (!activeTop) {
      return;
    }

    if (rootRef.current?.contains(event.target)) {
      return;
    }

    closeMenu();
  };

  const setTopSelection = item => {
    const secondLevelItems = getSortedChildren(item);
    const defaultSecond = secondLevelItems.length > 0 ? secondLevelItems[0] : null;

    setActiveTop(item);
    setActiveSecond(defaultSecond);
    setActiveThird(null);

    const defaultPreview = getNodePreview(item) || getNodePreview(defaultSecond);
    setPreviewSrc(defaultPreview);
  };

  const handleTopHover = (item, event) => {
    const target = event.currentTarget;
    clearAllTimers();

    const openMenu = () => {
      if (!target) {
        return;
      }

      setAnchorEl(rootRef.current || target);
      setTopSelection(item);
    };

    if (activeTop) {
      openMenu();
      return;
    }

    openTimer.current = setTimeout(openMenu, OPEN_DELAY);
  };

  const handleSecondHover = menuNode => {
    setActiveSecond(menuNode);
    setActiveThird(null);
    setPreviewSrc(getNodePreview(menuNode));
  };

  const handleThirdHover = menuNode => {
    setActiveThird(menuNode);
    setPreviewSrc(getNodePreview(menuNode) || getNodePreview(activeSecond));
  };

  const isTopItemActive = item => activeTop?.id === item?.id || activeTop?.name === item?.name;
  const isSecondItemActive = menuNode => activeSecond?.id === menuNode?.id;

  const handleTopClick = (item, event, itemLinkBehavior, hasChildren) => {
    if (!hasChildren) {
      scheduleClose();
      return;
    }

    if (!isTouchInteractionMode) {
      if (itemLinkBehavior.link) {
        scheduleClose();
      }
      return;
    }

    const isActive = isTopItemActive(item);

    if (!isActive) {
      event.preventDefault();
      event.stopPropagation();
      setAnchorEl(rootRef.current || event.currentTarget);
      setTopSelection(item);
      return;
    }

    if (!itemLinkBehavior.link) {
      event.preventDefault();
      event.stopPropagation();
      setAnchorEl(rootRef.current || event.currentTarget);
      setTopSelection(item);
      return;
    }

    scheduleClose();
  };

  const handleSecondClick = (menuNode, event, menuNodeLinkBehavior, hasNestedChildren) => {
    if (!hasNestedChildren) {
      scheduleClose();
      return;
    }

    if (!isTouchInteractionMode) {
      if (menuNodeLinkBehavior.link) {
        scheduleClose();
      }
      return;
    }

    const isActive = isSecondItemActive(menuNode);

    if (!isActive) {
      event.preventDefault();
      event.stopPropagation();
      handleSecondHover(menuNode);
      return;
    }

    if (!menuNodeLinkBehavior.link) {
      event.preventDefault();
      event.stopPropagation();
      handleSecondHover(menuNode);
      return;
    }

    scheduleClose();
  };

  const handleTopKeyDown = (item, event, itemLinkBehavior, hasChildren) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      return;
    }

    if (!hasChildren || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    const isActive = isTopItemActive(item);

    if (!isActive || !itemLinkBehavior.link) {
      event.preventDefault();
      setAnchorEl(rootRef.current || event.currentTarget);
      setTopSelection(item);
    }
  };

  const handleSecondKeyDown = (menuNode, event, menuNodeLinkBehavior, hasNestedChildren) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      return;
    }

    if (!hasNestedChildren || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    const isActive = isSecondItemActive(menuNode);

    if (!isActive || !menuNodeLinkBehavior.link) {
      event.preventDefault();
      handleSecondHover(menuNode);
    }
  };

  const handleImageError = () => {
    setPreviewSrc(null);
  };

  const secondLevelItems = getSortedChildren(activeTop);
  const thirdLevelItems = getSortedChildren(activeSecond);
  const secondLevelTitle = activeTop?.name;
  const thirdLevelTitle = activeSecond?.name;
  const previewName = activeThird?.name || activeSecond?.name || activeTop?.name || '';
  let navJustifyContent = 'center';

  if (align === 'left') {
    navJustifyContent = 'flex-start';
  } else if (align === 'right') {
    navJustifyContent = 'flex-end';
  }

  const getLinkComponent = linkBehavior => {
    if (!linkBehavior.link) {
      return 'button';
    }

    return linkBehavior.isExternalRoute ? 'a' : Link;
  };

  const getInteractiveLinkProps = (linkBehavior, options = {}) => {
    const { lowercaseInternalLink = false, requireLink = false } = options;

    if (!linkBehavior.link && requireLink) {
      return {};
    }

    if (linkBehavior.isExternalRoute) {
      return {
        href: linkBehavior.link,
        target: linkBehavior.target,
        rel: linkBehavior.rel,
      };
    }

    return {
      to: lowercaseInternalLink ? linkBehavior.link?.toLocaleLowerCase() : linkBehavior.link,
    };
  };

  const getNavItemClassName = isActive => `${classes.navItem} ${isActive ? classes.navItemActive : ''}`.trim();
  const getMenuButtonClassName = isActive => `${classes.menuButton} ${isActive ? classes.menuButtonActive : ''}`.trim();

  const renderTopItem = item => {
    const hasChildren = getSortedChildren(item).length > 0;
    const hasImage = Boolean(item?.id && getPreviewFileId(item));
    const isActive = isTopItemActive(item);
    const itemLinkBehavior = getNavLinkBehavior(item);

    if (!hasChildren && !hasImage && item?.link) {
      return (
        <ButtonBase
          key={item.name}
          className={getNavItemClassName(isActive)}
          component={itemLinkBehavior.isExternalRoute ? 'a' : Link}
          {...getInteractiveLinkProps(itemLinkBehavior, { lowercaseInternalLink: true })}
          data-tour={item.dataTour || undefined}
          onMouseEnter={isTouchInteractionMode ? undefined : scheduleClose}
          onClick={scheduleClose}>
          <Typography variant="h5" className={classes.navItemText}>
            {item.name}
          </Typography>
        </ButtonBase>
      );
    }

    return (
      <ButtonBase
        key={item.name}
        className={getNavItemClassName(isActive)}
        component={getLinkComponent(itemLinkBehavior)}
        {...getInteractiveLinkProps(itemLinkBehavior)}
        data-tour={item.dataTour || undefined}
        aria-haspopup="true"
        aria-expanded={isActive ? 'true' : undefined}
        onMouseEnter={isTouchInteractionMode ? undefined : event => handleTopHover(item, event)}
        onKeyDown={event => handleTopKeyDown(item, event, itemLinkBehavior, hasChildren)}
        onClick={event => handleTopClick(item, event, itemLinkBehavior, hasChildren)}>
        <Typography variant="h5" className={classes.navItemText}>
          {item.name}
        </Typography>
      </ButtonBase>
    );
  };

  const renderSecondLevelItem = menuNode => {
    const hasNestedChildren = getSortedChildren(menuNode).length > 0;
    const isActive = isSecondItemActive(menuNode);
    const menuNodeLinkBehavior = getNavLinkBehavior(menuNode);
    const leafLinkProps = menuNodeLinkBehavior.link
      ? {
          component: menuNodeLinkBehavior.isExternalRoute ? 'a' : Link,
          ...getInteractiveLinkProps(menuNodeLinkBehavior, { requireLink: true }),
        }
      : {};

    if (hasNestedChildren) {
      return (
        <ButtonBase
          key={menuNode.id || menuNode.name}
          className={getMenuButtonClassName(isActive)}
          component={getLinkComponent(menuNodeLinkBehavior)}
          {...getInteractiveLinkProps(menuNodeLinkBehavior)}
          aria-expanded={isActive ? 'true' : undefined}
          onMouseEnter={isTouchInteractionMode ? undefined : () => handleSecondHover(menuNode)}
          onKeyDown={event => handleSecondKeyDown(menuNode, event, menuNodeLinkBehavior, hasNestedChildren)}
          onClick={event => handleSecondClick(menuNode, event, menuNodeLinkBehavior, hasNestedChildren)}>
          <Typography variant="h5">{menuNode.name}</Typography>
          <ChevronRight fontSize="small" />
        </ButtonBase>
      );
    }

    return (
      <Box
        key={menuNode.id || menuNode.name}
        {...leafLinkProps}
        className={classes.menuLink}
        onMouseEnter={isTouchInteractionMode ? undefined : () => handleSecondHover(menuNode)}
        onClick={scheduleClose}>
        <ButtonBase className={getMenuButtonClassName(isActive)}>
          <Typography variant="h5">{menuNode.name}</Typography>
        </ButtonBase>
      </Box>
    );
  };

  const renderThirdLevelItem = menuNode => {
    const isActive = activeThird?.id === menuNode.id;
    const hasPreviewImage = Boolean(menuNode?.id && getPreviewFileId(menuNode));
    const menuNodeLinkBehavior = getNavLinkBehavior(menuNode);
    const leafLinkProps = menuNodeLinkBehavior.link
      ? {
          component: menuNodeLinkBehavior.isExternalRoute ? 'a' : Link,
          ...getInteractiveLinkProps(menuNodeLinkBehavior, { requireLink: true }),
        }
      : {};

    return (
      <Box
        key={menuNode.id || menuNode.name}
        {...leafLinkProps}
        className={classes.menuLink}
        onMouseEnter={isTouchInteractionMode ? undefined : () => handleThirdHover(menuNode)}
        onClick={scheduleClose}>
        <ButtonBase className={getMenuButtonClassName(isActive)}>
          <Typography variant="h5">{menuNode.name}</Typography>
          {hasPreviewImage ? <ChevronRight fontSize="small" /> : null}
        </ButtonBase>
      </Box>
    );
  };

  if (!matches) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        ref={rootRef}
        className={classes.root}
        sx={{
          justifyContent: navJustifyContent,
          backgroundColor: background,
        }}
        style={style}
        onMouseEnter={isTouchInteractionMode ? undefined : handlePopoverEnter}
        onMouseLeave={isTouchInteractionMode ? undefined : scheduleClose}>
        {sortedItems.map(renderTopItem)}

        <Popper
          open={Boolean(activeTop)}
          anchorEl={anchorEl}
          placement="bottom-start"
          disablePortal
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 0],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 0,
                altAxis: true,
                tether: true,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
              },
            },
          ]}
          style={{ zIndex: 1300 }}>
          <Paper
            className={classes.popoverPaper}
            onMouseEnter={isTouchInteractionMode ? undefined : handlePopoverEnter}
            onMouseLeave={isTouchInteractionMode ? undefined : scheduleClose}>
            <Box className={classes.megaContent}>
              <Box className={classes.column}>
                <Typography variant="h6" className={classes.columnTitle}>
                  {secondLevelTitle}
                </Typography>
                {secondLevelItems.map(renderSecondLevelItem)}
              </Box>

              <Box className={`${classes.column} ${classes.secondColumn}`.trim()}>
                <Typography variant="h6" className={classes.columnTitle}>
                  {thirdLevelTitle || ' '}
                </Typography>
                {thirdLevelItems.length > 0 ? (
                  thirdLevelItems.map(renderThirdLevelItem)
                ) : (
                  <Typography variant="body2" className={classes.noThirdLevel}></Typography>
                )}
              </Box>

              <Box className={`${classes.column} ${classes.thirdColumn}`.trim()}>
                {previewSrc ? (
                  <Box key={previewSrc} className={classes.imagePreviewWrap}>
                    <Box className={classes.imagePreviewCard}>
                      <Box className={classes.imagePreviewMedia}>
                        <img
                          src={previewSrc}
                          alt={t('dealerPortal.megaSiteNav.previewAlt')}
                          className={classes.image}
                          onError={handleImageError}
                        />
                        {previewName && (
                          <Box className={classes.previewOverlay}>
                            {previewName ? (
                              <Typography className={classes.previewCaption} variant="h2">
                                {previewName}
                              </Typography>
                            ) : null}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box className={classes.imagePreviewWrap}>
                    <Box className={classes.imageFallback}>
                      <Typography variant="h6">{t('dealerPortal.megaSiteNav.noImageAvailable')}</Typography>
                      <Typography variant="body2">{t('dealerPortal.megaSiteNav.hoverToUpdatePreview')}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

MegaSiteNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  background: PropTypes.string,
  color: PropTypes.string,
  contentBackground: PropTypes.string,
  contentColor: PropTypes.string,
  rowHeight: PropTypes.number,
  breakpoint: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  style: PropTypes.shape({}),
};

MegaSiteNav.defaultProps = {
  align: 'center',
  background: '#000',
  color: '#fff',
  contentBackground: '#1f1f1f',
  contentColor: '#fff',
  rowHeight: 50,
  breakpoint: 768,
  style: undefined,
};

export default MegaSiteNav;
